import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useI18n } from '@/i18n';
import { variboxSealOptions, type VariboxStatusKey } from '@/data/variboxMockData';
import { getLocationTree } from '@/services/locationApi';
import {
  getVariboxById,
  checkVariboxDuplicates,
  mapApiVariboxToView,
  updateVaribox,
  type UpdateVariboxPayload,
  type VariboxDuplicateFields,
  type VariboxViewRecord,
} from '@/services/variboxApi';

type ClientOption = { id: string; name: string };
type LocationOption = { id: string; label: string };

type FormState = {
  serialNumber: string;
  radexIdentifier: string;
  barcode: string;
  capacityL: string;
  color: string;
  sealType: string;
  status: VariboxStatusKey;
  contract: boolean;
  rfidTag: string;
  locationId: string;
  clientId: string;
  notes: string;
};

const emptyForm: FormState = {
  serialNumber: '',
  radexIdentifier: '',
  barcode: '',
  capacityL: '',
  color: '',
  sealType: 'VITON',
  status: 'available',
  contract: false,
  rfidTag: '',
  locationId: '',
  clientId: '',
  notes: '',
};

const statusOptions: VariboxStatusKey[] = ['available', 'occupied', 'damaged', 'repair', 'cleaning', 'blocked'];
const noDuplicates: VariboxDuplicateFields = { serial_number: false, radex_identifier: false, barcode: false };

function getLocationOptions(nodes: Awaited<ReturnType<typeof getLocationTree>>['items']): LocationOption[] {
  const options: LocationOption[] = [];

  const walk = (items: typeof nodes, depth: number, rootCode: string | null) => {
    for (const node of items) {
      const nextRootCode = depth === 0 ? node.code : rootCode;
      if (nextRootCode === 'PL01' || nextRootCode === 'PL99') {
        const prefix = depth > 0 ? `${'  '.repeat(depth - 1)}- ` : '';
        options.push({
          id: node.id,
          label: `${prefix}${node.code ? `${node.code} ` : ''}${node.name}`.trim(),
        });
      }
      walk(node.children, depth + 1, nextRootCode);
    }
  };

  walk(nodes, 0, null);
  return options;
}

export default function VariboxEditDialog({
  open,
  record,
  clientOptions,
  onClose,
  onUpdated,
}: {
  open: boolean;
  record: VariboxViewRecord | null;
  clientOptions: ClientOption[];
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { t } = useI18n();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [duplicateFields, setDuplicateFields] = useState(noDuplicates);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateCheckError, setDuplicateCheckError] = useState(false);

  useEffect(() => {
    if (!open || !record) {
      return;
    }

    let cancelled = false;
    setLoadingRecord(true);
    setErrorMessage('');

    const loadFormData = async () => {
      try {
        const [apiRecord, locationTree] = await Promise.all([
          getVariboxById(record.id),
          getLocationTree(),
        ]);

        if (cancelled) {
          return;
        }

        const locations = getLocationOptions(locationTree.items);
        if (apiRecord.current_location && !locations.some((location) => location.id === apiRecord.current_location?.id)) {
          const current = apiRecord.current_location;
          locations.push({
            id: current.id,
            label: `${current.code ? `${current.code} ` : ''}${current.name}`.trim(),
          });
        }

        setLocationOptions(locations);
        setForm({
          serialNumber: apiRecord.serial_number,
          radexIdentifier: apiRecord.radex_identifier,
          barcode: apiRecord.barcode,
          capacityL: String(apiRecord.capacity_l),
          color: apiRecord.color,
          sealType: apiRecord.seal_type,
          status: mapApiVariboxToView(apiRecord).status,
          contract: apiRecord.contract,
          rfidTag: apiRecord.rfid_tag ?? '',
          locationId: apiRecord.current_location?.id ?? '',
          clientId: apiRecord.current_client?.id ?? '',
          notes: apiRecord.notes ?? '',
        });
      } catch {
        if (!cancelled) {
          setErrorMessage(t('assets.editDialog.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoadingRecord(false);
        }
      }
    };

    void loadFormData();
    return () => {
      cancelled = true;
    };
  }, [open, record, t]);

  useEffect(() => {
    if (!open || !record) return;

    const serialNumber = form.serialNumber.trim();
    const radexIdentifier = form.radexIdentifier.trim();
    const barcode = form.barcode.trim();
    setDuplicateFields(noDuplicates);
    setCheckingDuplicates(false);
    setDuplicateCheckError(false);
    if (!serialNumber && !radexIdentifier && !barcode) return;
    setCheckingDuplicates(true);

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void checkVariboxDuplicates({
        serial_number: serialNumber,
        radex_identifier: radexIdentifier,
        barcode,
        exclude_id: record.id,
      })
        .then((duplicates) => {
          if (!cancelled) setDuplicateFields(duplicates);
        })
        .catch(() => {
          if (!cancelled) setDuplicateCheckError(true);
        })
        .finally(() => {
          if (!cancelled) setCheckingDuplicates(false);
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.barcode, form.radexIdentifier, form.serialNumber, open, record]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!record) {
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    const payload: UpdateVariboxPayload = {
      serial_number: form.serialNumber.trim(),
      radex_identifier: form.radexIdentifier.trim(),
      barcode: form.barcode.trim(),
      capacity_l: Number(form.capacityL),
      color: form.color.trim(),
      seal_type: form.sealType.trim(),
      status: form.status,
      contract: form.contract,
      rfid_tag: form.rfidTag.trim() || null,
      current_location_id: form.locationId || undefined,
      current_client_id: form.clientId || null,
      notes: form.notes.trim() || null,
    };

    try {
      await updateVaribox(record.id, payload);
      onUpdated();
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (detail === 'Duplicate serial_number') {
          setDuplicateFields((current) => ({ ...current, serial_number: true }));
          setErrorMessage(t('assets.editDialog.duplicateSerial'));
        } else if (detail === 'Duplicate radex_identifier') {
          setDuplicateFields((current) => ({ ...current, radex_identifier: true }));
          setErrorMessage(t('assets.editDialog.duplicateRadex'));
        } else if (detail === 'Duplicate barcode') {
          setDuplicateFields((current) => ({ ...current, barcode: true }));
          setErrorMessage(t('assets.editDialog.duplicateBarcode'));
        } else if (typeof detail === 'string' && detail.trim()) {
          setErrorMessage(detail);
        } else {
          setErrorMessage(t('assets.editDialog.error'));
        }
      } else {
        setErrorMessage(t('assets.editDialog.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(
    form.serialNumber.trim()
      && form.radexIdentifier.trim()
      && form.barcode.trim()
      && Number(form.capacityL) > 0
      && form.color.trim()
      && form.sealType.trim()
      && !duplicateFields.serial_number
      && !duplicateFields.radex_identifier
      && !duplicateFields.barcode,
  );
  const disabled = loadingRecord || submitting;

  return (
    <Dialog open={open} onClose={disabled ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assets.editDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('assets.editDialog.description')}
          </Typography>

          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          {duplicateCheckError ? <Alert severity="warning">{t('assets.editDialog.duplicateCheckError')}</Alert> : null}
          {checkingDuplicates ? <Typography variant="caption" color="text.secondary">{t('assets.editDialog.checkingDuplicates')}</Typography> : null}
          {duplicateFields.serial_number || duplicateFields.radex_identifier || duplicateFields.barcode ? (
            <Alert severity="error">{t('assets.editDialog.duplicateWarning')}</Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField required label={t('assets.createDialog.serialNumber')} value={form.serialNumber} onChange={(event) => update('serialNumber', event.target.value)} error={duplicateFields.serial_number} helperText={duplicateFields.serial_number ? t('assets.editDialog.duplicateSerial') : undefined} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField required label={t('assets.createDialog.radexIdentifier')} value={form.radexIdentifier} onChange={(event) => update('radexIdentifier', event.target.value)} error={duplicateFields.radex_identifier} helperText={duplicateFields.radex_identifier ? t('assets.editDialog.duplicateRadex') : undefined} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField required label={t('assets.createDialog.barcode')} value={form.barcode} onChange={(event) => update('barcode', event.target.value)} error={duplicateFields.barcode} helperText={duplicateFields.barcode ? t('assets.editDialog.duplicateBarcode') : undefined} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField required type="number" inputProps={{ min: 1, step: 1 }} label={t('assets.createDialog.capacity')} value={form.capacityL} onChange={(event) => update('capacityL', event.target.value)} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField required label={t('assets.createDialog.color')} value={form.color} onChange={(event) => update('color', event.target.value)} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select required label={t('assets.createDialog.sealType')} value={form.sealType} onChange={(event) => update('sealType', event.target.value)} disabled={disabled} fullWidth>
                {!variboxSealOptions.includes(form.sealType as (typeof variboxSealOptions)[number]) ? <MenuItem value={form.sealType}>{form.sealType}</MenuItem> : null}
                {variboxSealOptions.map((sealType) => <MenuItem key={sealType} value={sealType}>{sealType}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.status')} value={form.status} onChange={(event) => update('status', event.target.value as VariboxStatusKey)} disabled={disabled} fullWidth>
                {statusOptions.map((status) => <MenuItem key={status} value={status}>{t(`assets.statuses.${status}`)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.contract')} value={form.contract ? 'yes' : 'no'} onChange={(event) => update('contract', event.target.value === 'yes')} disabled={disabled} fullWidth>
                <MenuItem value="yes">{t('assets.contractValues.tak')}</MenuItem>
                <MenuItem value="no">{t('assets.contractValues.nie')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t('assets.createDialog.rfidTag')} value={form.rfidTag} onChange={(event) => update('rfidTag', event.target.value)} disabled={disabled} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.editDialog.location')} value={form.locationId} onChange={(event) => update('locationId', event.target.value)} disabled={disabled} fullWidth>
                <MenuItem value="">{t('assets.editDialog.keepLocation')}</MenuItem>
                {locationOptions.map((location) => <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select label={t('assets.createDialog.client')} value={form.clientId} onChange={(event) => update('clientId', event.target.value)} disabled={disabled} fullWidth>
                <MenuItem value="">{t('assets.createDialog.noClient')}</MenuItem>
                {clientOptions.map((client) => <MenuItem key={client.id} value={client.id}>{client.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField multiline minRows={3} label={t('assets.createDialog.notes')} value={form.notes} onChange={(event) => update('notes', event.target.value)} disabled={disabled} fullWidth />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={disabled}>{t('common.buttons.close')}</Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={!canSubmit || disabled || checkingDuplicates}>
          {submitting ? t('assets.editDialog.saving') : checkingDuplicates ? t('assets.editDialog.checkingDuplicates') : t('assets.editDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
